/**
 * Agapē Community brand constants + host detection.
 *
 * Some events (livestreams, live rooms, practices) are organized by the
 * Agapē Community itself rather than by a specific master. When that's the
 * case, the announcement hero and the chat circle should show the community
 * brand (logo + "Сообщество Agapē") instead of whatever generic placeholder
 * the backend returned for `hostName`.
 */

import type { ImageSourcePropType } from 'react-native';

/**
 * Numeric master-profile id reserved for the Agapē Community account.
 *
 * CORE-142 — this MUST come from the build-time env, because the community's
 * MasterProfile.Id differs per backend (dev = 7, prod = 9). It used to be
 * hardcoded to the dev value `7`, so the production bundle pointed community
 * links / brand detection at a non-existent master id → wrong/empty profiles.
 * Mirrors the `EXPO_PUBLIC_AGAPE_COMMUNITY_USER_ID` wiring in
 * AgapePracticeScreen; keep both env vars set together on each environment.
 */
export const AGAPE_COMMUNITY_MASTER_ID: number = (() => {
  const raw = process.env.EXPO_PUBLIC_AGAPE_COMMUNITY_MASTER_ID;
  const n = raw ? Number(raw) : NaN;
  return Number.isFinite(n) && n > 0 ? n : 7; // dev default
})();

export const COMMUNITY_DISPLAY_NAME_RU = 'Сообщество Agapē';
export const COMMUNITY_DISPLAY_NAME_EN = 'Agapē Community';

/** Community logo asset — works with React Native `<Image source={...} />`. */
export const COMMUNITY_LOGO: ImageSourcePropType = require('../../assets/brand/logo_back.webp');

/**
 * Heuristic: given a host user id + display name from the backend, decide
 * whether this event is organized by the Agapē Community. Matches on the
 * reserved master id (primary signal) and a set of common display-name
 * patterns (fallback, tolerant of locale / casing).
 */
export const isCommunityHost = (
  hostUserId?: string | number | null,
  hostName?: string | null,
): boolean => {
  if (hostUserId != null) {
    const idStr = String(hostUserId).trim().toLowerCase();
    if (idStr === String(AGAPE_COMMUNITY_MASTER_ID)) return true;
    if (idStr === 'agapella' || idStr === 'community' || idStr === 'agape-community') return true;
  }
  const nameLower = (hostName || '').trim().toLowerCase();
  if (!nameLower) return false;
  // Direct brand matches
  if (
    nameLower === 'agapella' ||
    nameLower === 'агапелла' ||
    nameLower === 'agapē' ||
    nameLower === 'agape' ||
    nameLower === 'агапэ' ||
    nameLower.includes('сообщество agap') ||
    nameLower.includes('agapē community') ||
    nameLower.includes('agape community') ||
    nameLower.includes('agapē сообщ') ||
    nameLower.includes('сообщество agapē')
  ) return true;
  // Known test/dev aliases for the community account on agdev backend.
  // TODO: remove once backend exposes an explicit `isCommunityEvent` flag on
  // RoomResponse / LiveDetailResponse.
  if (COMMUNITY_TEST_ALIASES.includes(nameLower)) return true;
  return false;
};

/**
 * Dev-backend test accounts that manage community events but are not named
 * after the brand. Keep this list tight and temporary — production should
 * use a proper `isCommunityEvent` boolean from the backend.
 */
const COMMUNITY_TEST_ALIASES: string[] = [
  'g falls',
  'gfalls',
];

export const getCommunityDisplayName = (ru: boolean): string =>
  ru ? COMMUNITY_DISPLAY_NAME_RU : COMMUNITY_DISPLAY_NAME_EN;
